import React from "react";
import Box from "react-raster";
import { theme } from "src/config/theme";
import styled from "styled-components";
import { debounce, getCaretPos } from "src/util/util";
import { User, Comment } from "src/models";
import ReactDOM from "react-dom";


interface ITextAreaProps {
  defaultValue?: string;
  disabled?:     boolean;
  label?:        string;
  maxLength?:    number;
  rows?:         number;
  color?:        any;
  mentionOptions?: User[];
  onChange:   (value: string) => void;
  onMention?: (data: User) => void;
}

interface IMention {id: number, label: string, data: User, color: string};
export const TextArea = React.forwardRef<{setContent: (text: string) => void}, ITextAreaProps>((
  { maxLength, defaultValue, onChange, disabled, label, rows, onMention, mentionOptions }: ITextAreaProps, ref) => {
    
    const [caretPos,   setCaretPos]   = React.useState<DOMRect>(null);
    const [sel,        setSel]        = React.useState<Selection>(null);
    const [text,       setText]       = React.useState<string>(defaultValue || "");
    const [mentionPre, setMentionPre] = React.useState<string>(null);
    const [listIndex,  setListIndex]  = React.useState<number>(-1);
    
    const dropDownRefs        = (mentionOptions||[]).map(() => React.useRef<HTMLLIElement>());
    const textAreaRef         = React.useRef<HTMLDivElement>();
    const containerRef        = React.useRef<HTMLDivElement>();
    const mentionContainerRef = React.useRef<HTMLUListElement>();

    const debouncedChange = React.useCallback(debounce(onChange), []);
    const showMentions    = Boolean(mentionOptions);
    
    // const handlePasteEvent  = (e: React.ClipboardEvent<HTMLDivElement>) => { 
    //   e.preventDefault();
    //   document.execCommand("insertText", false, e.clipboardData.getData('Text').slice(0, maxLength - text.length));
    // }

    const handlePasteEvent = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const clipboardData = e.clipboardData.getData('text/plain');
      textAreaRef.current.innerText = clipboardData
      setText(clipboardData);
    }

    const handleKeyPress    = (e: React.KeyboardEvent<HTMLDivElement>)  => { 
      setSel(window.getSelection());
      if(maxLength && textAreaRef.current.innerText.length >= maxLength) {e.preventDefault(); return;} 
    }

    const handleControlKeys = (e: React.KeyboardEvent<HTMLDivElement>)  => {
      if(e.keyCode === 38 && mentionPre !== null) { // up
        const newIdx = (listIndex+mentions.length-1) % mentions.length;
        mentionContainerRef.current.scrollTo(0, dropDownRefs[newIdx].current.offsetTop);
        setListIndex(newIdx); e.preventDefault(); e.stopPropagation();
      } else if(e.keyCode === 40 && mentionPre !== null) { // down
        const newIdx = (listIndex+1) % mentions.length;
        mentionContainerRef.current.scrollTo(0, dropDownRefs[newIdx].current.offsetTop);
        setListIndex(newIdx); e.preventDefault(); e.stopPropagation();
      } else if(e.keyCode === 13 && listIndex >= 0 && mentionPre !== null) {// enter
        confirmMention();
        e.preventDefault(); e.stopPropagation();
      }
    }
    const handleSelectionChange = () => {
      if(!showMentions) return;
      const curSel: Selection = document.getSelection();
      if(!curSel.rangeCount) return;
      
      const inputIdx: number = curSel.getRangeAt(0).startOffset; // Selection index in current line
      const curLine:  string = curSel.getRangeAt(0).startContainer.textContent; // String of the current line only 
      const curWord:  string = curLine.slice(0, inputIdx).split(" ").pop(); // Word the char before the caret belongs to. Empty string if the char before the caret is a space

      if(curWord === Comment.mentionIdentifier) { setCaretPos(getCaretPos()); setMentionPre("");} // Mention begins
      else if(curWord === Comment.mentionIdentifier) { setCaretPos(getCaretPos()); setMentionPre("");}
      else if(curWord.startsWith(Comment.mentionIdentifier)) setMentionPre(curWord.slice(1) ); // Mention continues
      else { setMentionPre(null); setCaretPos(null); setListIndex(-1);} // Mention ended
    }
    
    const getPos = (caretPos: DOMRect): {top: number, left: number} => ({
      top:  caretPos && caretPos.top,
      left: caretPos && caretPos.left
    });

    React.useImperativeHandle(ref, () => ({
      setContent: (text: string): void => {
        if(!textAreaRef.current) return;
        textAreaRef.current.innerText = text;
        setText(text);
        if(!text.length) return;
        const range: Range     = document.createRange();
        const sel:   Selection = window.getSelection();
        range.setStart(textAreaRef.current.childNodes[0], text.length);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }));
    React.useEffect(() => { if(textAreaRef.current) textAreaRef.current.innerText = text; }, []);
    React.useEffect(() => debouncedChange(text), [text]);
    const mentions: IMention[] = React.useMemo(() => {
      if(!showMentions) return [];
      return mentionOptions.filter(o => o && o.getFullName && o.getFullName.startsWith(mentionPre)).map(u => ( {id: u.id, label: u.getFullName, data: u} as IMention))
    }, [mentionOptions, mentionPre]);

    const confirmMention = React.useCallback(() => {
      document.execCommand("insertText", false, mentions[listIndex].label.slice(mentionPre.length) + " ");
      onMention && onMention(mentions[listIndex].data);
    }, [mentions, onMention, listIndex]);

    return (
      <Container ref={containerRef}>
        <Label color={disabled ? theme.color.disabled : undefined}>{label}</Label>
        <StyledTextArea 
          rows       = {rows}
          disabled   = {disabled}
          ref        = {textAreaRef}
          onKeyPress = {handleKeyPress}
          onKeyDown  = {handleControlKeys}
          onSelect   = {handleSelectionChange}
          onInput    = {() => setText(textAreaRef.current.innerText)}
          onPaste    = {handlePasteEvent}
          contentEditable 
        />
        <CharCounterContainer>
          {maxLength && <CharCounter>{`${text.length} / ${maxLength}`}</CharCounter>}
        </CharCounterContainer>
        {
          ReactDOM.createPortal((
            <MentionListContainer pos={getPos(caretPos)} hidden={mentionPre === null} ref={mentionContainerRef}>
            {mentions.map((m, idx) => (
              <MentionListEntry 
                onMouseDown  = {(e) => e.preventDefault()}
                onClick      = {() => confirmMention()}
                key          = {m.id}
                ref          = {dropDownRefs[idx]}
                selected     = {listIndex === idx}
                onMouseEnter = {() => {
                  setListIndex(idx);
                  document.getSelection();
                }}
              >
                {m.label}
              </MentionListEntry>
            ))}
          </MentionListContainer>
          ), document.body)
        }
      </Container>
    );
  }
);

const MentionListContainer = styled.ul<{pos: {top: number, left: number}}>`
  background-color: ${theme.color.white};
  border-radius:    0.4rem;
  box-shadow:       ${theme.boxShadow.small};
  color:            ${theme.color.black};
  height:           fit-content;
  left:             ${props => `calc(${props.pos.left}px)`};
  max-height:       16rem;
  min-width:        12rem;
  position:         fixed;
  top:              ${props => `calc(${props.pos.top}px + 2rem)`};
  z-index:          501;
  overflow:         auto;
`;

const MentionListEntry = styled.li<{selected: boolean}>`
  padding:        0.8rem;
  background:     ${props => props.selected ? theme.color.hover : undefined};
  font-size:      1rem;
  display:        flex;
  flex-direction: row;
  :hover {
    cursor: pointer;
  }
  :last-child {
    border-radius: 0 0 0.4rem 0.4rem;
  }
  :first-child {
    border-radius: 0.4rem 0.4rem 0 0;
  }
`;

const Label = styled.label<{color: string}>`
  background-color: ${theme.color.white};
  color:            ${props => props.color || "#757575"};
  font-size:        1.2rem;
  left:             0.7rem;
  margin-top:       -36px;
  padding:          0 8px;
  pointer-events:   none;
  position:         absolute;
  transform-origin: 0% 0%;
  transform:        translateY(26px) scale(1);
  transition:       transform 0.2s ease;
  z-index:          2;
`;

const StyledTextArea = styled.div<{disabled: boolean, rows?: number }>`
  border:           1px solid ${theme.color.input.border};
  border-radius:    0.25rem;
  font-size:        ${theme.font.size.small};
  background-color: white;
  width:            100%;
  padding:          0.8rem 0.8rem;
  height:           fit-content;
  min-height:       ${props => props.rows ? `calc(${props.rows} * 1.25rem + 1.6rem)` : undefined};
  white-space:      pre-wrap;
  line-height:      ${theme.font.size.large};
  color:            ${props => props.disabled ? theme.color.disabled : undefined};

:focus {
  outline: none !important;
  border: 1px solid ${theme.color.black};
}
`; 

const CharCounter = styled.span`
  font-size: ${theme.font.size.small};
  color:     ${theme.font.color.slate};
`;

const CharCounterContainer = styled(Box)`
  display:         flex;
  justify-content: flex-end;
  margin-top:      0.25rem;
  margin-right:    0.25rem;
  letter-spacing:  -0.5px;
`;

const Container = styled.div`
  position:       relative;
  min-width:      100%;
  display:        flex;
  flex-direction: column;
`;